import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  LogOut, Download, Search, ChevronDown, Trash2, RefreshCw, Send,
  Phone, Mail, CalendarDays, StickyNote, Users, CheckCircle2, Loader2,
} from "lucide-react";

const STATUSES = {
  nouveau: { label: "Nouveau", cls: "text-amber border-amber/40 bg-amber/10" },
  contacte: { label: "Contacté", cls: "text-sky-300 border-sky-400/40 bg-sky-400/10" },
  devis_envoye: { label: "Devis envoyé", cls: "text-amber-warm border-amber-warm/40 bg-amber-warm/10" },
  gagne: { label: "Gagné", cls: "text-emerald-300 border-emerald-400/40 bg-emerald-400/10" },
  perdu: { label: "Perdu", cls: "text-red-300 border-red-400/40 bg-red-400/10" },
};

const fmtDate = (iso) =>
  new Date(iso).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" });

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [notesDraft, setNotesDraft] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.insurance_type = typeFilter;
      if (search) params.q = search;
      const [leadsRes, statsRes] = await Promise.all([
        api.get("/admin/leads", { params }),
        api.get("/admin/stats"),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 401) navigate("/admin/login");
      else toast.error("Erreur de chargement des leads");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter, search, navigate]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const updateLead = async (id, patch) => {
    setBusyId(id);
    try {
      await api.patch(`/admin/leads/${id}`, patch);
      toast.success("Lead mis à jour");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Supprimer définitivement ce lead ?")) return;
    setBusyId(id);
    try {
      await api.delete(`/admin/leads/${id}`);
      toast.success("Lead supprimé");
      setExpandedId(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const pushCrm = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/admin/leads/${id}/push-crm`);
      toast.success("Lead envoyé vers OGGO Data");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const toggleExpand = (lead) => {
    if (expandedId === lead.id) {
      setExpandedId(null);
    } else {
      setExpandedId(lead.id);
      setNotesDraft(lead.notes || "");
    }
  };

  const statCards = stats
    ? [
        { key: "", label: "Total", value: stats.total, testid: "stats-card-total" },
        { key: "nouveau", label: "Nouveaux", value: stats.by_status.nouveau || 0, testid: "stats-card-nouveau" },
        { key: "contacte", label: "Contactés", value: stats.by_status.contacte || 0, testid: "stats-card-contacte" },
        { key: "devis_envoye", label: "Devis envoyés", value: stats.by_status.devis_envoye || 0, testid: "stats-card-devis" },
        { key: "gagne", label: "Gagnés", value: stats.by_status.gagne || 0, testid: "stats-card-gagne" },
        { key: "perdu", label: "Perdus", value: stats.by_status.perdu || 0, testid: "stats-card-perdu" },
      ]
    : [];

  return (
    <div className="min-h-screen bg-obsidian text-ivory" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 glass border-b hairline">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-baseline gap-3">
            <a href="/" className="font-serif text-2xl font-semibold tracking-tight" data-testid="admin-brand">
              ASSU<span className="text-amber inline-block" style={{ transform: "translateY(-0.1em)" }}>–</span>FRANCE
            </a>
            <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-[0.25em] text-ivory/40">
              Espace Courtier
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden md:block text-sm text-ivory/50" data-testid="admin-user-email">{user?.email}</span>
            <button
              onClick={() => window.open(`${API}/admin/leads/export`, "_blank")}
              data-testid="export-csv-button"
              className="inline-flex items-center gap-2 rounded-full border border-ivory/15 px-5 py-2.5 text-sm text-ivory/70 transition-all duration-300 hover:border-amber/50 hover:text-amber"
            >
              <Download className="h-4 w-4" /> Export CSV
            </button>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 rounded-full border border-ivory/15 px-5 py-2.5 text-sm text-ivory/70 transition-all duration-300 hover:border-red-400/50 hover:text-red-300"
            >
              <LogOut className="h-4 w-4" /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 sm:px-8 py-10">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-10" data-testid="stats-grid">
          {statCards.map((c) => (
            <button
              key={c.testid}
              data-testid={c.testid}
              onClick={() => setStatusFilter(c.key)}
              className={`rounded-2xl border p-5 text-left transition-all duration-300 hover:-translate-y-1 ${
                statusFilter === c.key ? "border-amber/60 bg-amber/10" : "hairline bg-cardbg hover:border-ivory/25"
              }`}
            >
              <p className="font-serif text-3xl font-semibold text-amber">{c.value}</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-ivory/50">{c.label}</p>
            </button>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-4 mb-8" data-testid="leads-filters">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-ivory/30" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un nom, email ou téléphone…"
              data-testid="lead-search-input"
              className="w-full rounded-xl border border-ivory/10 bg-white/5 pl-11 pr-4 py-3 text-sm text-ivory placeholder:text-ivory/30 gold-focus transition-all duration-300"
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            data-testid="lead-type-filter"
            className="rounded-xl border border-ivory/10 bg-cardbg px-4 py-3 text-sm text-ivory gold-focus"
          >
            <option value="">Tous les types</option>
            {[
              "Assurance Automobile",
              "Assurance Habitation",
              "Complémentaire Santé",
              "Assurance Vie & Épargne",
              "Assurance Professionnels / Entreprise",
              "Pack Multirisque Global",
            ].map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <button
            onClick={() => { setLoading(true); load(); }}
            data-testid="leads-refresh-button"
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-ivory/15 px-4 py-3 text-sm text-ivory/70 hover:text-amber hover:border-amber/50 transition-all duration-300"
          >
            <RefreshCw className="h-4 w-4" /> Actualiser
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-24" data-testid="leads-loading">
            <Loader2 className="h-8 w-8 animate-spin text-amber" />
          </div>
        ) : leads.length === 0 ? (
          <div className="rounded-3xl border hairline bg-cardbg py-24 text-center" data-testid="leads-empty">
            <Users className="mx-auto h-12 w-12 text-ivory/20" />
            <p className="mt-4 font-serif text-2xl text-ivory/60">Aucun lead pour le moment</p>
            <p className="mt-2 text-sm text-ivory/40">Les demandes de devis du site apparaîtront ici en temps réel.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="leads-list">
            {leads.map((lead) => {
              const st = STATUSES[lead.status] || STATUSES.nouveau;
              const expanded = expandedId === lead.id;
              return (
                <motion.div
                  key={lead.id}
                  layout
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-2xl border hairline bg-cardbg overflow-hidden"
                  data-testid={`lead-row-${lead.id}`}
                >
                  <button
                    onClick={() => toggleExpand(lead)}
                    data-testid={`lead-expand-${lead.id}`}
                    className="w-full px-5 sm:px-7 py-5 grid sm:grid-cols-[1.2fr_1fr_1fr_auto] gap-3 items-center text-left hover:bg-white/[0.02] transition-colors"
                  >
                    <div>
                      <p className="font-semibold text-ivory">{lead.full_name}</p>
                      <p className="text-xs text-ivory/40 mt-0.5 flex items-center gap-2">
                        <CalendarDays className="h-3 w-3" /> {fmtDate(lead.created_at)}
                      </p>
                    </div>
                    <p className="text-sm text-ivory/60">{lead.insurance_type}</p>
                    <span className={`w-fit rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] ${st.cls}`} data-testid={`lead-status-${lead.id}`}>
                      {st.label}
                    </span>
                    <ChevronDown className={`h-5 w-5 text-ivory/30 transition-transform duration-300 justify-self-end ${expanded ? "rotate-180" : ""}`} />
                  </button>

                  <AnimatePresence>
                    {expanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                        className="border-t hairline"
                        data-testid={`lead-detail-${lead.id}`}
                      >
                        <div className="px-5 sm:px-7 py-6 grid lg:grid-cols-2 gap-8">
                          <div className="space-y-3 text-sm">
                            <p className="flex items-center gap-3 text-ivory/70">
                              <Mail className="h-4 w-4 text-amber" /> {lead.email}
                            </p>
                            <p className="flex items-center gap-3 text-ivory/70">
                              <Phone className="h-4 w-4 text-amber" /> {lead.phone}
                            </p>
                            <div className="rounded-xl bg-white/[0.03] border hairline p-4 text-ivory/60 leading-relaxed">
                              {lead.message || "Aucun message."}
                            </div>
                            {lead.details && Object.keys(lead.details).length > 0 && (
                              <div className="rounded-xl bg-white/[0.03] border hairline p-4" data-testid={`lead-details-${lead.id}`}>
                                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber/70 mb-3">
                                  Réponses au questionnaire
                                </p>
                                <ul className="space-y-1.5 text-sm text-ivory/70">
                                  {Object.entries(lead.details).map(([k, v]) => (
                                    <li key={k}>
                                      <span className="text-ivory/40">{k} :</span>{" "}
                                      {Array.isArray(v) ? v.join(", ") : String(v)}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            <p className="flex items-center gap-2 text-xs text-ivory/40" data-testid={`lead-crm-state-${lead.id}`}>
                              {lead.crm_synced_at ? (
                                <>
                                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-300" />
                                  Synchronisé OGGO Data le {fmtDate(lead.crm_synced_at)}
                                </>
                              ) : (
                                "Non synchronisé avec OGGO Data"
                              )}
                            </p>
                          </div>

                          <div className="space-y-4">
                            <div>
                              <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-ivory/40">
                                Statut du lead
                              </label>
                              <select
                                value={lead.status}
                                onChange={(e) => updateLead(lead.id, { status: e.target.value })}
                                disabled={busyId === lead.id}
                                data-testid={`lead-status-select-${lead.id}`}
                                className="w-full rounded-xl border border-ivory/10 bg-cardbg px-4 py-3 text-sm text-ivory gold-focus"
                              >
                                {Object.entries(STATUSES).map(([k, v]) => (
                                  <option key={k} value={k}>{v.label}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="mb-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ivory/40">
                                <StickyNote className="h-3 w-3" /> Notes internes
                              </label>
                              <textarea
                                value={notesDraft}
                                onChange={(e) => setNotesDraft(e.target.value)}
                                rows={3}
                                placeholder="Compte-rendu d'appel, relances…"
                                data-testid={`lead-notes-input-${lead.id}`}
                                className="w-full rounded-xl border border-ivory/10 bg-white/5 px-4 py-3 text-sm text-ivory placeholder:text-ivory/30 gold-focus resize-none"
                              />
                            </div>
                            <div className="flex flex-wrap gap-3">
                              <button
                                onClick={() => updateLead(lead.id, { notes: notesDraft })}
                                disabled={busyId === lead.id}
                                data-testid={`lead-notes-save-${lead.id}`}
                                className="rounded-full bg-amber px-5 py-2.5 text-sm font-semibold text-obsidian transition-all duration-300 hover:bg-amber-warm disabled:opacity-50"
                              >
                                Enregistrer la note
                              </button>
                              <button
                                onClick={() => pushCrm(lead.id)}
                                disabled={busyId === lead.id}
                                data-testid={`lead-push-crm-${lead.id}`}
                                className="inline-flex items-center gap-2 rounded-full border border-ivory/15 px-5 py-2.5 text-sm text-ivory/70 hover:border-amber/50 hover:text-amber transition-all duration-300 disabled:opacity-50"
                              >
                                <Send className="h-4 w-4" /> Envoyer vers OGGO
                              </button>
                              <button
                                onClick={() => deleteLead(lead.id)}
                                disabled={busyId === lead.id}
                                data-testid={`lead-delete-${lead.id}`}
                                className="inline-flex items-center gap-2 rounded-full border border-red-400/30 px-5 py-2.5 text-sm text-red-300 hover:bg-red-400/10 transition-all duration-300 disabled:opacity-50"
                              >
                                <Trash2 className="h-4 w-4" /> Supprimer
                              </button>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
